export const LOCALES = ["en", "es", "pt", "de"] as const;

export type Locale = (typeof LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

export const LOCALE_COOKIE_NAME = "NEXT_LOCALE";

export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365;

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  es: "Español",
  pt: "Português",
  de: "Deutsch",
};

export const LOCALE_SHORT_LABELS: Record<Locale, string> = {
  en: "EN",
  es: "ES",
  pt: "PT",
  de: "DE",
};

export function localeShortLabel(locale: string): string {
  if (isLocale(locale)) {
    return LOCALE_SHORT_LABELS[locale];
  }

  return locale.trim().toUpperCase();
}

export const LOCALE_BCP47: Record<Locale, string> = {
  en: "en-US",
  es: "es",
  pt: "pt-BR",
  de: "de-DE",
};

export function toBcp47Locale(locale: Locale): string {
  return LOCALE_BCP47[locale];
}

export interface AcceptLanguageTag {
  tag: string;
  quality: number;
}

export function isLocale(value: string | null | undefined): value is Locale {
  return LOCALES.some((locale) => locale === value);
}

export function parseAcceptLanguage(
  header: string | null | undefined,
): AcceptLanguageTag[] {
  if (!header?.trim()) {
    return [];
  }

  return header
    .split(",")
    .map((part) => {
      const [rawTag, ...params] = part.trim().split(";");
      const tag = rawTag?.trim().toLowerCase() ?? "";
      const qualityParam = params.find((param) =>
        param.trim().toLowerCase().startsWith("q="),
      );
      const quality = qualityParam
        ? Number.parseFloat(qualityParam.trim().slice(2))
        : 1;
      return { tag, quality: Number.isFinite(quality) ? quality : 0 };
    })
    .filter((entry) => entry.tag.length > 0 && entry.tag !== "*")
    .sort((left, right) => right.quality - left.quality);
}

function localeFromTag(tag: string): Locale | null {
  if (isLocale(tag)) {
    return tag;
  }

  const language = tag.split("-")[0];
  return isLocale(language) ? language : null;
}

export function negotiateLocale(
  acceptLanguage: string | null | undefined,
): Locale {
  for (const { tag } of parseAcceptLanguage(acceptLanguage)) {
    const locale = localeFromTag(tag);
    if (locale) {
      return locale;
    }
  }

  return DEFAULT_LOCALE;
}

export function resolveRequestLocale({
  cookieLocale,
  acceptLanguage,
}: {
  cookieLocale?: string | null;
  acceptLanguage?: string | null;
}): Locale {
  if (isLocale(cookieLocale)) {
    return cookieLocale;
  }

  return negotiateLocale(acceptLanguage);
}

export function buildLocaleCookie(locale: Locale): string {
  return `${LOCALE_COOKIE_NAME}=${locale}; Path=/; Max-Age=${LOCALE_COOKIE_MAX_AGE}; SameSite=Lax`;
}

export function shouldPersistLocaleCookie(pathname: string): boolean {
  return (
    !pathname.startsWith("/api/") && !pathname.startsWith("/.well-known/")
  );
}
